import { createContext, useCallback, useContext, useState, type ReactElement, type ReactNode } from 'react';
import { Button } from './ui';

/**
 * App-weiter Bestätigungsdialog — ersetzt das native confirm() durch ein
 * sauberes, gebrandetes Modal (wichtig fuer Desktop-/Mobile-App via Capacitor,
 * wo native Browser-Dialoge unschoen bzw. nicht verfuegbar sind).
 *
 * Nutzung:  const confirm = useConfirm();
 *           if (await confirm({ title: '…', message: '…' })) { … }
 */

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export function ConfirmProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }));
  }, []);

  function close(result: boolean) {
    state?.resolve(result);
    setState(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => close(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900">{state.opts.title}</h2>
            {state.opts.message && <p className="mt-2 text-sm text-gray-600">{state.opts.message}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => close(false)}>
                {state.opts.cancelLabel ?? 'Abbrechen'}
              </Button>
              <Button variant={state.opts.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
                {state.opts.confirmLabel ?? 'OK'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm muss innerhalb von <ConfirmProvider> verwendet werden.');
  return ctx;
}
